"use client";

import { cn } from "@/lib/utils";
import { usePageLoading } from "@/hooks/usePageLoading.hook";
import { useAnimatedLogo } from "@/components/hooks/useAnimatedLogo.hook";

interface AnimatedLogoProps {
  width?: number;
  height?: number;
  className?: string;
  onClick?: () => void;
}


const AnimatedLogo: React.FC<AnimatedLogoProps> = ({
  width = 130,
  height = 24,
  className,
  onClick,
}: Readonly<AnimatedLogoProps>) => {
  const { isLoading, isAnimating } = usePageLoading();
  const { handleClick } = useAnimatedLogo({ onClick });

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 132 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("cursor-pointer transition-all duration-300 hover:scale-105 focus:outline-none", className)}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      aria-label="PLANA Logo - Click to go to dashboard"
      data-testid="animated-logo"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* P Letter */}
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1 22.9474H2.92481V13.9248H24.0977V1H1V22.9474ZM2.92481 2.92481H22.1729V12H2.92481V2.92481Z"
        fill="black"
      />
      
      {/* L Letter */}
      <path d="M29.9248 1.02344H28V22.9708H44.3609V21.046H29.9248V1.02344Z" fill="black" />
      
      {/* A Letters with Animation */}
      {isLoading && isAnimating ? (
        <>
          {/* Left A - Blue animated stroke */}
          <path
            d="M48 23.0234H50.4204L60.5357 4.83113L70.6607 22.9962L73.0714 23.0234L60.5357 1.02344L48 23.0234Z"
            stroke="#00A1E8"
            strokeWidth="2"
            strokeDasharray="18"
            strokeDashoffset="0"
            fill="none"
            className="animate-logo-stroke"
          />
          {/* Right A - Red animated stroke */}
          <path
            d="M103.93 23.0234H106.35L116.465 4.83112L126.59 22.9961L129.001 23.0234L116.465 1.02344L103.93 23.0234Z"
            stroke="#FF6252"
            strokeWidth="2"
            strokeDasharray="18"
            strokeDashoffset="0"
            fill="none"
            className="animate-logo-stroke"
          />
        </>
      ) : (
        <>
          {/* Left A - Blue filled */}
          <path
            d="M48 23.0234H50.4204L60.5357 4.83113L70.6607 22.9962L73.0714 23.0234L60.5357 1.02344L48 23.0234Z"
            fill="black"
          />
          {/* Right A - Red filled */}
          <path
            d="M103.93 23.0234H106.35L116.465 4.83112L126.59 22.9961L129.001 23.0234L116.465 1.02344L103.93 23.0234Z"
            fill="black"
          />
        </>
      )}
      
      {/* N Letter */}
      <path
        d="M77.0098 22.9708V1.02344L98.2556 10.5724V1.02348H100.18V22.9398H98.2556V12.746L79.0075 4.1995V22.9708H77.0098Z"
        fill="black"
      />
      
      {/* Colored Triangle Fills */}
      <path d="M60.4994 14.0234L65.2625 23.0234H55.7363L60.4994 14.0234Z" fill="#00A1E8" />
      <path d="M116.499 14.0234L121.262 23.0234H111.736L116.499 14.0234Z" fill="#FF6252" />
      
      {/* Trademark Symbol */}
      <path d="M126 1.47509V1H128.046V1.47509H127.312V3.42036H126.735V1.47509H126Z" fill="black" />
      <path
        d="M128.33 1H129.055L129.669 2.49854H129.698L130.312 1H131.037V3.42036H130.467V1.93363H130.447L129.866 3.405H129.502L128.92 1.92536H128.9V3.42036H128.33V1Z"
        fill="black"
      />
    </svg>
  );
};

export default AnimatedLogo;
